"use client";

import { IconInfoCircle } from "@tabler/icons-react";
import {
  Spinner,
  Button,
  Link,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@heroui/react";
import { ResponsiveLine } from "@nivo/line";
import dayjs from "dayjs";
import { formatPrice } from "@/lib/moneyHelper";
import { PriceHistory } from "@/models/price-history";
import { getTCGPlayerUrl } from "@/lib/tcgplayer/tcgplayerAffiliateLink";

function createDateWithMDY(dateString: string) {
  // return new Date(dateString);
  return dayjs(dateString).format("YYYY-MM-DD");
}

function getMonthDay(date: string) {
  return dayjs(date).format("MM/DD");
}

type point = {
  x: number | string | Date;
  y: number;
};

type nivoData = {
  id: string;
  data: point[];
};

export const getMaxValue = (nivoData: nivoData[]) => {
  let ys = [];
  for (const line of nivoData) {
    for (const point of line.data) {
      ys.push(point.y);
    }
  }
  const max = Math.max(...ys);
  return max * 1.1;
};

export const getMinValue = (nivoData: nivoData[]) => {
  let ys = [];
  for (const line of nivoData) {
    for (const point of line.data) {
      ys.push(point.y);
    }
  }
  const max = Math.min(...ys);
  return max * 0.9;
};

export default function PriceHistoryChart({
  tcgplayerId,
  cardPriceHistory,
  isLoaded,
}: {
  tcgplayerId: string | null;
  cardPriceHistory: PriceHistory[];
  isLoaded: boolean;
}) {
  if (isLoaded) {
    return (
      <div className="flex justify-center items-center min-h-[388px]">
        <Spinner />
      </div>
    );
  }
  if (!cardPriceHistory || cardPriceHistory.length === 0) {
    return <div>No market data available</div>;
  }

  const priceHistory: point[] = cardPriceHistory.map((entry: PriceHistory) => {
    return {
      x: createDateWithMDY(entry.d),
      y: entry.l,
    };
  });

  const firstLowPrice: number = priceHistory[0].y;

  const y2: point[] = cardPriceHistory.map((entry: PriceHistory) => {
    return {
      x: createDateWithMDY(entry.d),
      y: entry.m,
    };
  });

  let data: nivoData[] = [
    { id: "low price", data: priceHistory },
    { id: "market price", data: y2 },
  ];

  const theme = {
    background: "transparent",
    crosshair: {
      line: {
        stroke: "#ffffff",
        strokeWidth: 2,
        // strokeOpacity: 0.35,
      },
    },
    text: {
      fontSize: 12,
      fill: "#ffffff",
      outlineWidth: 0,
      outlineColor: "#000000",
    },
    axis: {
      textColor: "#fff",
      fontSize: 12,
      tickColor: "#fff",
      domain: {
        line: {
          stroke: "#fff",
          strokeWidth: 1,
        },
      },
      legend: {
        text: {
          fontSize: 12,
          fill: "#fff",
          outlineWidth: 0,
          outlineColor: "transparent",
        },
      },
      ticks: {
        line: {
          stroke: "#ffffff",
          strokeWidth: 0,
        },
        text: {
          fontSize: 12,
          fill: "#ffffff",
          outlineWidth: 0,
          outlineColor: "#ffffff",
        },
      },
    },
    grid: {
      line: {
        stroke: "#dddddd",
        strokeWidth: 1,
      },
    },
    legends: {
      title: {
        text: {
          fontSize: 12,
          fill: "#ffffff",
          outlineWidth: 0,
          outlineColor: "#ffffff",
        },
      },
      text: {
        fontSize: 12,
        fill: "#ffffff",
        outlineWidth: 0,
        outlineColor: "#ffffff",
      },
      ticks: {
        line: {},
        text: {
          fontSize: 10,
          fill: "#ffffff",
          outlineWidth: 0,
          outlineColor: "transparent",
        },
      },
    },
    annotations: {
      text: {
        fontSize: 12,
        fill: "#333333",
        outlineWidth: 2,
        outlineColor: "#ffffff",
        outlineOpacity: 1,
      },
      link: {
        stroke: "#000000",
        strokeWidth: 1,
        outlineWidth: 2,
        outlineColor: "#ffffff",
        outlineOpacity: 1,
      },
      outline: {
        stroke: "#ffffff",
        strokeWidth: 2,
        outlineWidth: 2,
        outlineColor: "#ffffff",
        outlineOpacity: 1,
      },
      symbol: {
        fill: "#000000",
        outlineWidth: 2,
        outlineColor: "#ffffff",
        outlineOpacity: 1,
      },
    },
    tooltip: {
      container: {
        // background: "#000000",
        background: "rgb(30 41 59)",
        // rgb(15 23 42 / var(--tw-text-opacity))
        color: "#ffffff",
        fontSize: 12,
      },
      basic: {},
      chip: {},
      table: {},
      tableCell: {},
      tableCellValue: {},
    },
  };

  let x = (
    <ResponsiveLine
      theme={theme}
      sliceTooltip={({ slice }) => {
        return (
          <div
            className="p-2 border border-slate-700 text-sm"
            style={{ backgroundColor: "rgb(24, 24, 27, 0.8)" }}
          >
            {getMonthDay(slice.points[0].data.x.toString())}
            {slice.points.map((point) => (
              <div
                key={point.id}
                className={"flex flex-row items-center gap-2 w-[180px]"}
              >
                <div className={"w-2/3 flex flex-row items-center"}>
                  <div
                    className={`w-2 h-2 mr-2 rounded-full gap-1`}
                    style={{ backgroundColor: point.borderColor }}
                  ></div>
                  {point.seriesId}:
                </div>
                <div className={"w-1/3"}>
                  {formatPrice(parseFloat(point.data.y.toString()))}
                </div>
              </div>
            ))}
          </div>
        );
      }}
      data={data}
      margin={{ top: 60, right: 50, bottom: 50, left: 60 }}
      xScale={{
        format: "%Y-%m-%d",
        // format: "native",
        type: "time",
        precision: "day",
        useUTC: false,
      }}
      xFormat="time:%Y-%m-%d"
      yScale={{
        type: "linear",
        min: getMinValue(data),
        max: getMaxValue(data),
        stacked: false,
        reverse: false,
      }}
      yFormat=" >-$,.2f"
      curve="monotoneX"
      axisTop={null}
      axisRight={null}
      axisBottom={{
        tickValues: 5,
        tickSize: 5,
        tickPadding: 5,
        tickRotation: -45,
        // legend: "Date",
        format: "%m/%d",
        legendOffset: 36,
        legendPosition: "middle",
        truncateTickAt: 0,
      }}
      axisLeft={{
        tickValues: 5,
        tickSize: 7,
        tickPadding: 0,
        tickRotation: 0,
        // legend: "Price",
        format: ">-$,.2f",
        legendOffset: -40,
        legendPosition: "middle",
        truncateTickAt: 0,
      }}
      enablePoints={false}
      enableGridX={false}
      enableGridY={false}
      pointColor={{ from: "color" }}
      pointBorderWidth={1}
      pointBorderColor={{ from: "serieColor" }}
      pointLabelYOffset={-12}
      enableSlices="x"
      enableTouchCrosshair={true}
      useMesh={true}
      colors={["rgb(148, 85, 211)", "rgb(0, 112, 240)"]}
      legends={[
        {
          anchor: "top",
          direction: "row",
          justify: false,
          translateX: 0,
          translateY: -40,
          itemWidth: 100,
          itemHeight: 20,
          itemsSpacing: 4,
          symbolSize: 16,
          symbolShape: "circle",
          itemDirection: "left-to-right",
          itemTextColor: "#FFF",
          effects: [
            {
              on: "hover",
              style: {
                itemBackground: "rgba(0, 0, 0, .03)",
                itemOpacity: 1,
              },
            },
          ],
        },
      ]}
      motionConfig="default"
    />
  );
  return (
    <div>
      <div className="flex flex-col justify-center items-center font-medium">
        <div className="text-2xl">{formatPrice(firstLowPrice)}</div>
        <div className="text-secondary text-xs">Low Price</div>
      </div>
      <div className="h-[300px] min-w-[340px]">{x}</div>
      {tcgplayerId && PriceHistoryChart.length > 0 && (
        <div className="flex justify-center items-center gap-2">
          <div className="h-[24px] w-[24px]"></div>
          <Button
            onPress={() => {
              if (typeof window !== "undefined" && window.gtag) {
                window.gtag("event", "buy_card", {
                  value: firstLowPrice,
                });
              }
            }}
            as={Link}
            href={getTCGPlayerUrl(parseInt(tcgplayerId), "market-watch")}
            color="primary"
          >
            View on TCGPlayer
          </Button>
          <Popover>
            <PopoverTrigger>
              <IconInfoCircle />
            </PopoverTrigger>
            <PopoverContent>
              <div>This link supports gumgum.gg!</div>
            </PopoverContent>
          </Popover>
        </div>
      )}
    </div>
  );
}
